import { faCopy, faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip } from "antd";
import { showError, showSuccess } from "common/ToastNotifications";
import ShareDataObjectModalDialog from "components/organisms/ShareDataObjectModalDialog";
import dayjs, { Dayjs } from "dayjs";
import { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { Action, Dispatch } from "redux";
import actions from "store/actions";
import AppState from "store/AppState";

const mapStateToProps = (state: AppState) => {
    return {
        getDataObjectSharedLinkResult: state.dataObjects.getDataObjectSharedLinkResult,
        dataObjectSharedLinkResult: state.dataObjects.dataObjectSharedLinkResult,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action<string>>) => {
    return {
        getDataObjectSharedLink: (businessAreaId: string, schemaId: string, dataSetId: string, dataObjectId: string, expires: string, readOnly: boolean) =>
            dispatch(actions.getDataObjectSharedLinkRequest(businessAreaId, schemaId, dataSetId, dataObjectId, expires, readOnly)),
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type IPropsFromRedux = ConnectedProps<typeof connector>;

interface IDataObjectShareOptionProps extends IPropsFromRedux {
    businessAreaId: string;
    schemaId: string;
    dataSetId: string;
    dataObjectId: string;
    children?: React.ReactNode;
    size?: "small" | "middle" | "large";
    dataTestid?: string;
    getDataObjectSharedLink: (businessAreaId: string, schemaId: string, dataSetId: string, dataObjectId: string, expiryDate: string, readOnly: boolean) => void;
}

type IDataObjectShareOptionStates = {
    showShareModalDialog: boolean;
    gettingLink: boolean;
    shareLink: string | null;
};

class DataObjectShareOption extends Component<IDataObjectShareOptionProps & RouteComponentProps, IDataObjectShareOptionStates> {
    constructor(props: IDataObjectShareOptionProps & RouteComponentProps) {
        super(props);
        this.state = {
            showShareModalDialog: false,
            gettingLink: false,
            shareLink: null,
        };
    }

    componentDidUpdate(prevProps: IDataObjectShareOptionProps & RouteComponentProps, prevState: unknown, snapshot: unknown) {
        this.onGetDataObjectSharedLinkResultChanged(prevProps);
    }

    onGetDataObjectSharedLinkResultChanged = (prevProps: IDataObjectShareOptionProps & RouteComponentProps) => {
        if (this.props.getDataObjectSharedLinkResult && this.props.getDataObjectSharedLinkResult !== prevProps.getDataObjectSharedLinkResult) {
            if (!this.props.getDataObjectSharedLinkResult.success) {
                showError("Unable to get share link.");
            } else {
                if (this.props.dataObjectSharedLinkResult) {
                    this.setState({
                        shareLink: this.props.dataObjectSharedLinkResult.url,
                    });
                } else {
                    showError("Unable to get share link.");
                }
            }
            this.setState({
                gettingLink: false,
            });
        }
    };

    onShareClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
        e.stopPropagation && e.stopPropagation();
        e.preventDefault && e.preventDefault();
        this.setState({ showShareModalDialog: true, shareLink: null });
    };

    onCopyClick: React.MouseEventHandler<HTMLSpanElement> = async (e) => {
        e.stopPropagation && e.stopPropagation();
        e.preventDefault && e.preventDefault();
        try {
            await navigator.clipboard.writeText(this.props.dataObjectId);
            showSuccess("Record Id copied to clipboard");
        } catch (e) {
            showError("Failed to copy Record Id");
        }
    };

    onGetLink = ({ readOnly, expiryDate }: { readOnly: boolean; expiryDate: Dayjs }) => {
        if (this.state.gettingLink) {
            return;
        }
        this.setState({
            gettingLink: true,
        });
        this.props.getDataObjectSharedLink(
            this.props.businessAreaId,
            this.props.schemaId,
            this.props.dataSetId,
            this.props.dataObjectId,
            expiryDate ? expiryDate.utc().format("YYYYMMDDHHmmss") : dayjs().add(5, "minutes").utc().format("YYYYMMDDHHmmss"),
            readOnly,
        );
    };

    onClose = () => {
        this.setState({
            shareLink: null,
            showShareModalDialog: false,
        });
    };

    render() {
        return (
            <>
                <ShareDataObjectModalDialog
                    show={this.state.showShareModalDialog}
                    onGetLink={this.onGetLink}
                    loading={this.state.gettingLink}
                    shareLink={this.state.shareLink}
                    onClose={this.onClose}
                ></ShareDataObjectModalDialog>
                <div className="flex items-center gap-2">
                    <Tooltip title="Share link">
                        <Button
                            type="link"
                            size={this.props.size ?? "small"}
                            icon={<FontAwesomeIcon icon={faShareNodes} />}
                            onClick={this.onShareClick}
                            data-testid={this.props.dataTestid}
                        />
                    </Tooltip>
                    <Tooltip title="Copy Record Id">
                        <Button
                            type="link"
                            size={this.props.size ?? "small"}
                            icon={<FontAwesomeIcon icon={faCopy} />}
                            onClick={this.onCopyClick}
                            data-testid={`${this.props.dataTestid}-copy`}
                        />
                    </Tooltip>
                    {this.props.children}
                </div>
            </>
        );
    }
}

export default connector(withRouter(DataObjectShareOption));
